import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { ChatMessage } from '@/interface';
import { useDispatch, useSelector } from 'react-redux';
import { authDataSelector } from '@/services/redux/selectors';
import { Text } from '../Text';
import { cn, formatMessageTime } from '@/utils/common_functions';
import { markChatMessagesAsDelivered } from '@/services/redux/slices/chatSlice';
import { BULLET, ChatMessageStatus, ChatMessageType, UserRole } from '@/constants';
import { useSocket, useToast } from '@/hooks';
import { updateMessageEvent } from '@/services/socket';
import { Icon } from '../Icon';
import { Video, AlertTriangle } from 'lucide-react';
import { CancelMeetingModal } from './CancelMeetingModal';

interface MessageProps extends ChatMessage {
  consultation_id: number;
  other_user_name?: string;
}

export const Message: React.FC<MessageProps> = props => {
  const { body, created_at, sender_id, status, id, consultation_id, type, other_user_name } = props;

  // hooks
  const dispatch = useDispatch();
  const { sendMessage } = useSocket();
  const { showToast } = useToast();

  // selectors
  const authData = useSelector(authDataSelector);

  // states
  const user_id = authData?.id;
  const isSender = sender_id === user_id;
  const isPatient = authData?.role === UserRole.PATIENT;
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelMeetingId, setCancelMeetingId] = useState<number | null>(null);

  const confirmCancelMeeting = async (reason: string, overrideId?: number) => {
    const targetId = overrideId || cancelMeetingId;
    if (!targetId) return;
    setIsCancelling(true);
    try {
      const { cancelMeeting, requestCancelMeeting } = await import('@/services/api');
      if (!isPatient) {
        await cancelMeeting(targetId, reason);
        showToast('Meeting cancelled successfully', 'success');
      } else {
        await requestCancelMeeting(targetId, reason);
        showToast('Meeting cancellation requested', 'success');
      }
      setIsCancelModalOpen(false);
    } catch(e: any) {
      console.error(e);
      const msg = e?.response?.data?.message || 'Failed to cancel meeting';
      showToast(msg, 'failure');
    } finally {
      setIsCancelling(false);
      setCancelMeetingId(null);
    }
  };

  const messageBody = useMemo<ReactNode>(() => {
    if (type === ChatMessageType.DocumentRequest) {
      const points = body.split(BULLET.trim());
      return <div className="flex flex-col gap-1 p-1">
        <div className='flex gap-2 items-center'>
          <Icon className='size-4' type={isSender ? "file_arrow_up_white" : "file_arrow_up_blue"} />
          <Text className=''>Please upload the following reports : </Text>
        </div>
        <ul className='list-disc ml-10'>
          {points.map(point => <li><Text>{point}</Text></li>)}
        </ul>
      </div>
    }
    if (type === ChatMessageType.VideoMeeting) {
      let link = body;
      let time = '';
      let meeting_id: number | null = null;
      let isParsed = false;
      try {
        console.log("VideoMeeting body:", body);
        const parsed = JSON.parse(body);
        console.log("VideoMeeting parsed:", parsed);
        link = parsed.link || '';
        time = parsed.time || '';
        meeting_id = parsed.meeting_id || null;
        isParsed = true;
      } catch(e) {
        console.error("VideoMeeting parse error:", e);
      }
      
      if (link && !link.startsWith('http://') && !link.startsWith('https://')) {
        link = `https://${link}`;
      }
      
      const handleCancelMeeting = () => {
        if (!meeting_id) return;
        setCancelMeetingId(meeting_id);
        setIsCancelModalOpen(true);
      };
      
      const isPastMeeting = time ? new Date(time) < new Date() : false;
      const formattedTime = time ? new Date(time).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '';
      const doctorNameText = (isPatient && other_user_name) ? other_user_name : (isSender ? "You have" : "Dr. has");

      return <div className="flex flex-col gap-2 p-2 min-w-[200px]">
          <div className="flex items-center gap-2">
             <Video className="size-5" />
             <Text className="!font-FigtreeBold">Video Consultation</Text>
          </div>
          <Text className="text-sm">
            {isSender 
              ? `You have scheduled a video consultation for ${formattedTime}.`
              : `Dr. ${other_user_name || 'Specialist'} has scheduled a video consultation for ${formattedTime}. Please use the button below to join.`}
          </Text>
          {link ? (
            <a href={link} target="_blank" rel="noreferrer" className={cn("block px-4 py-2 rounded-lg font-FigtreeBold text-center mt-2 w-full hover:opacity-90 cursor-pointer", isSender ? "bg-white text-ui_blue" : "bg-ui_blue text-white")}>
              Join Meeting
            </a>
          ) : (
            <div className={cn("block px-4 py-2 rounded-lg font-FigtreeBold text-center mt-2 w-full opacity-50 cursor-not-allowed", isSender ? "bg-white text-ui_blue" : "bg-ui_blue text-white")}>
              Join Meeting (No Link)
            </div>
          )}
          {!isPastMeeting && meeting_id && (
            <button 
              onClick={handleCancelMeeting} 
              className="mt-2 w-full px-4 py-2 rounded-lg font-FigtreeBold text-center bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700 transition-colors"
            >
              {isSender ? "Cancel Meeting" : "Request Cancellation"}
            </button>
          )}
      </div>
    }
    if (type === ChatMessageType.CancellationRequest) {
      let reason = '';
      let meeting_id: number | null = null;
      try {
        const parsed = JSON.parse(body);
        reason = parsed.reason || '';
        meeting_id = parsed.meeting_id || null;
      } catch(e) {
        reason = body;
      }

      const handleCancelMeeting = () => {
        if (!meeting_id) {
          alert("This cancellation request is from an older version. Please scroll up and use the 'Cancel Meeting' button in the original meeting message.");
          return;
        }
        // Doctor is accepting patient's cancellation request directly
        confirmCancelMeeting(reason ? `Patient request: ${reason}` : "Cancelled upon patient's request", meeting_id);
      };

      return <div className="flex flex-col gap-3 p-1 min-w-[250px]">
        <div className="flex items-center gap-2">
           <AlertTriangle className={cn("size-5", isSender ? "text-[#FCD34D]" : "text-[#F59E0B]")} />
           <Text className={cn("!font-FigtreeBold", isSender ? "text-[#FCD34D]" : "text-[#F59E0B]")}>Meeting Cancellation Requested</Text>
        </div>
        <Text className="text-sm whitespace-pre-wrap">
          {isSender 
            ? `You requested to cancel the meeting.` 
            : `The patient is requesting to cancel or reschedule the upcoming video consultation.`}
        </Text>
        {reason && (
          <div className={cn("px-4 py-3 rounded-xl mt-1 text-sm italic", isSender ? "bg-white/20 text-white" : "bg-white text-ui_blue shadow-sm")}>
            <span className="font-FigtreeBold not-italic">Reason:</span> {reason}
          </div>
        )}
        {!isSender && (
          <button 
            onClick={handleCancelMeeting} 
            className="mt-2 w-full px-4 py-2.5 rounded-xl font-FigtreeBold text-center bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-colors"
          >
            Cancel Meeting
          </button>
        )}
      </div>
    }
    return <Text variant="body" className="!font-FigtreeMeduim whitespace-pre-wrap">
      {body}
    </Text>

  }, [body, type]);


  // mark message as delivered
  useEffect(() => {
    if (status === ChatMessageStatus.Sent && sender_id !== user_id) {
      dispatch(markChatMessagesAsDelivered({
        consultation_id,
        message_id: id,
      }));

      // emit event to server
      sendMessage(updateMessageEvent(id, { status: ChatMessageStatus.Delivered }));
    }
  }, [status, id]);



  return (
    <>
      <div className={cn('flex flex-col gap-2', isSender ? 'items-end' : 'items-start')}>
        <div className={cn(
          "max-w-[90%] py-2 px-3 rounded-xl",
          isSender ? 'bg-gradient-to-r from-ui_blue to-ui_blue_light rounded-br-none text-white' : 'bg-[#EDEFF5] rounded-bl-none text-ui_blue')}>
          {messageBody}
        </div>

        {/* extra message to be appended for document request */}
        {isPatient && type === ChatMessageType.DocumentRequest && !isSender && <div className={cn(
          "max-w-[90%] py-2 px-3 rounded-xl",
          isSender ? 'bg-gradient-to-r from-ui_blue to-ui_blue_light rounded-br-none text-white' : 'bg-[#EDEFF5] rounded-bl-none text-ui_blue')}>
          <Text variant="body">
            You can upload them using <span className='font-FigtreeBold italic'>'Upload additional document'</span> button.
          </Text>
        </div>}

        {/* time */}
        <Text variant="tiny" className="!font-FigtreeMeduim text-ui_placeholder">
          {formatMessageTime(created_at)}
        </Text>
      </div>

      <CancelMeetingModal 
        open={isCancelModalOpen} 
        onClose={() => setIsCancelModalOpen(false)} 
        onConfirm={confirmCancelMeeting} 
        isLoading={isCancelling} 
        isRequest={!isSender}
      />
    </>
  );
};
